import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Package, ChevronRight, Filter, Search, Calendar,
  IndianRupee, ShoppingBag, Sparkles, Clock, CheckCircle2,
  Truck, XCircle, RefreshCw, ArrowRight, LayoutGrid
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

/* ─── helpers ─────────────────────────────────────────────── */
const STATUS_META = {
  pending:    { label: 'Pending',    icon: Clock,         bg: 'rgba(251,191,36,.15)',  border: 'rgba(251,191,36,.4)',  text: '#b45309', dot: '#f59e0b' },
  confirmed:  { label: 'Confirmed',  icon: CheckCircle2,  bg: 'rgba(59,130,246,.12)',  border: 'rgba(59,130,246,.4)', text: '#1d4ed8', dot: '#3b82f6' },
  processing: { label: 'Processing', icon: RefreshCw,     bg: 'rgba(168,85,247,.12)',  border: 'rgba(168,85,247,.4)', text: '#7c3aed', dot: '#a855f7' },
  shipped:    { label: 'Shipped',    icon: Truck,         bg: 'rgba(99,102,241,.12)',  border: 'rgba(99,102,241,.4)', text: '#4338ca', dot: '#6366f1' },
  delivered:  { label: 'Delivered',  icon: CheckCircle2,  bg: 'rgba(16,185,129,.12)',  border: 'rgba(16,185,129,.4)', text: '#065f46', dot: '#10b981' },
  cancelled:  { label: 'Cancelled',  icon: XCircle,       bg: 'rgba(239,68,68,.10)',   border: 'rgba(239,68,68,.4)',  text: '#991b1b', dot: '#ef4444' },
  refunded:   { label: 'Refunded',   icon: RefreshCw,     bg: 'rgba(107,114,128,.12)', border: 'rgba(107,114,128,.4)',text: '#374151', dot: '#9ca3af' },
};

const PAYMENT_META = {
  paid:        { label: 'Paid',              color: '#059669' },
  cod_pending: { label: 'Cash on Delivery',  color: '#2563eb' },
  pending:     { label: 'Payment Pending',   color: '#d97706' },
  failed:      { label: 'Payment Failed',    color: '#dc2626' },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: '3px 10px', borderRadius: '20px', fontSize: '11px',
      fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase',
      background: m.bg, border: `1px solid ${m.border}`, color: m.text,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.dot, flexShrink: 0 }} />
      {m.label}
    </span>
  );
};

/* ─── page ──────────────────────────────────────────────────── */
const OrdersPage = () => {
  const { user, session } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (user && session) fetchOrders();
  }, [user, session, statusFilter, page]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/orders`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      const result = await response.json();
      if (result.success) {
        setOrders(result.orders);
        setPagination(result.pagination);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(o =>
    o.order_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /* ── not logged in ── */
  if (!user) return (
    <div style={styles.pageWrap}>
      <div style={styles.auroraOrb1} />
      <div style={styles.auroraOrb2} />
      <div style={{ ...styles.glassCard, maxWidth: 440, margin: '80px auto', textAlign: 'center', padding: 48 }}>
        <div style={styles.emptyIcon}><ShoppingBag size={40} color="#f97316" /></div>
        <h2 style={styles.emptyTitle}>Login Required</h2>
        <p style={styles.emptySubtitle}>Please login to view your orders</p>
        <Link to="/login" style={styles.primaryBtn}>Login to Continue</Link>
      </div>
    </div>
  );

  return (
    <div style={styles.pageWrap}>
      {/* ambient orbs */}
      <div style={styles.auroraOrb1} />
      <div style={styles.auroraOrb2} />
      <div style={styles.auroraOrb3} />

      <div style={styles.container}>

        {/* ── Header ── */}
        <div style={styles.header}>
          <div>
            <div style={styles.headerBadge}>
              <LayoutGrid size={13} />
              Order History
            </div>
            <h1 style={styles.pageTitle}>My Orders</h1>
            <p style={styles.pageSubtitle}>Track, manage and review all your purchases</p>
          </div>
          {pagination && (
            <div style={styles.orderCountBadge}>
              <span style={styles.orderCountNum}>{pagination.total ?? filteredOrders.length}</span>
              <span style={styles.orderCountLabel}>Total Orders</span>
            </div>
          )}
        </div>

        {/* ── Filters ── */}
        <div style={styles.filterCard}>
          <div style={styles.filterRow}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}><Filter size={13} /> Filter by Status</label>
              <select
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                style={styles.select}
              >
                <option value="">All Orders</option>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}><Search size={13} /> Search by Order No.</label>
              <div style={styles.searchWrap}>
                <Search size={15} style={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="e.g. ORD-20260904-..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={styles.searchInput}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div style={styles.loadingWrap}>
            <div style={styles.spinner} />
            <p style={{ color: '#64748b', marginTop: 16, fontSize: 14 }}>Loading your orders…</p>
          </div>
        )}

        {/* ── Error ── */}
        {error && !loading && (
          <div style={styles.errorCard}>{error}</div>
        )}

        {/* ── Orders ── */}
        {!loading && !error && (
          <>
            {filteredOrders.length === 0 ? (
              <div style={{ ...styles.glassCard, textAlign: 'center', padding: '64px 32px' }}>
                <div style={styles.emptyIcon}><Package size={42} color="#f97316" /></div>
                <h3 style={styles.emptyTitle}>
                  {searchQuery || statusFilter ? 'No matching orders' : 'No Orders Yet'}
                </h3>
                <p style={styles.emptySubtitle}>
                  {searchQuery || statusFilter
                    ? 'Try adjusting your search or filter'
                    : 'Time to treat yourself — explore our top picks!'}
                </p>
                <Link to="/" style={styles.primaryBtn}>
                  <Sparkles size={15} /> Start Shopping
                </Link>
              </div>
            ) : (
              <div style={styles.orderList}>
                {filteredOrders.map((order, i) => {
                  const pm = PAYMENT_META[order.payment_status] || { label: order.payment_status, color: '#64748b' };
                  const isHov = hovered === order.id;
                  return (
                    <Link
                      key={order.id}
                      to={`/orders/${order.order_number}`}
                      style={{ textDecoration: 'none' }}
                      onMouseEnter={() => setHovered(order.id)}
                      onMouseLeave={() => setHovered(null)}
                    >
                      <div style={{
                        ...styles.orderCard,
                        transform: isHov ? 'translateY(-3px)' : 'translateY(0)',
                        boxShadow: isHov
                          ? '0 20px 60px rgba(249,115,22,.12), 0 4px 20px rgba(0,0,0,.08)'
                          : '0 4px 20px rgba(0,0,0,.06)',
                        borderColor: isHov ? 'rgba(249,115,22,.35)' : 'rgba(255,255,255,.7)',
                        animationDelay: `${i * 60}ms`,
                      }}>

                        {/* card top stripe */}
                        <div style={{
                          ...styles.cardStripe,
                          opacity: isHov ? 1 : 0,
                        }} />

                        {/* ── Order header ── */}
                        <div style={styles.orderHeader}>
                          <div style={styles.orderMeta}>
                            <div style={styles.orderNumRow}>
                              <div style={styles.orderIconWrap}>
                                <Package size={16} color="#f97316" />
                              </div>
                              <h3 style={styles.orderNum}>{order.order_number}</h3>
                              <StatusBadge status={order.status} />
                            </div>
                            <div style={styles.orderMetaRow}>
                              <span style={styles.metaChip}>
                                <Calendar size={12} />
                                {new Date(order.created_at).toLocaleDateString('en-IN', {
                                  day: 'numeric', month: 'short', year: 'numeric'
                                })}
                              </span>
                              <span style={styles.metaDot}>·</span>
                              <span style={styles.metaChip}>
                                {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                              </span>
                              <span style={styles.metaDot}>·</span>
                              <span style={{ ...styles.metaChip, color: pm.color, fontWeight: 700 }}>
                                {pm.label}
                              </span>
                            </div>
                          </div>
                          <div style={styles.orderAmount}>
                            <div style={styles.amountLabel}>Total</div>
                            <div style={styles.amountValue}>
                              <IndianRupee size={18} />
                              {parseFloat(order.total_amount).toLocaleString('en-IN', {
                                minimumFractionDigits: 2, maximumFractionDigits: 2
                              })}
                            </div>
                          </div>
                        </div>

                        {/* ── Items preview ── */}
                        {order.items && order.items.length > 0 && (
                          <div style={styles.itemsPreview}>
                            {order.items.slice(0, 3).map((item, idx) => (
                              <div key={idx} style={styles.itemRow}>
                                <div style={styles.itemImgWrap}>
                                  {item.image_url ? (
                                    <img
                                      src={item.image_url}
                                      alt={item.product_name}
                                      style={styles.itemImg}
                                      onError={e => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  <div style={{ ...styles.itemImgFallback, display: item.image_url ? 'none' : 'flex' }}>
                                    <Package size={18} color="#cbd5e1" />
                                  </div>
                                </div>
                                <div style={styles.itemInfo}>
                                  <p style={styles.itemName}>{item.product_name}</p>
                                  <div style={styles.itemMeta}>
                                    {item.variant_name && <span style={styles.variantTag}>{item.variant_name}</span>}
                                    <span style={styles.qtyTag}>×{item.quantity}</span>
                                    {item.unit_price && (
                                      <span style={styles.priceTag}>
                                        ₹{parseFloat(item.unit_price).toLocaleString('en-IN')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                            {order.items.length > 3 && (
                              <div style={styles.moreItems}>
                                +{order.items.length - 3} more item{order.items.length - 3 !== 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                        )}

                        {/* ── Footer ── */}
                        <div style={styles.cardFooter}>
                          <span style={styles.viewDetailsBtn}>
                            View Details <ArrowRight size={14} />
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* ── Pagination ── */}
            {pagination && pagination.totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{ ...styles.pageBtn, opacity: page === 1 ? .4 : 1 }}
                >← Prev</button>
                <div style={styles.pageNumbers}>
                  {[...Array(pagination.totalPages)].map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setPage(idx + 1)}
                      style={{
                        ...styles.pageNumBtn,
                        background: page === idx + 1 ? 'linear-gradient(135deg,#f97316,#ea580c)' : 'rgba(255,255,255,.8)',
                        color: page === idx + 1 ? '#fff' : '#374151',
                        fontWeight: page === idx + 1 ? 800 : 600,
                      }}
                    >{idx + 1}</button>
                  ))}
                </div>
                <button
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                  style={{ ...styles.pageBtn, opacity: page === pagination.totalPages ? .4 : 1 }}
                >Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* ─── styles ─────────────────────────────────────────────────── */
const styles = {
  pageWrap: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #fef9f0 0%, #fff7ed 30%, #fdf4ff 60%, #eff6ff 100%)',
    position: 'relative',
    overflow: 'hidden',
    paddingBottom: 80,
  },
  auroraOrb1: {
    position: 'fixed', top: -100, right: -100,
    width: 500, height: 500, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(251,146,60,.18) 0%, transparent 70%)',
    pointerEvents: 'none', zIndex: 0,
  },
  auroraOrb2: {
    position: 'fixed', bottom: -150, left: -100,
    width: 600, height: 600, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(168,85,247,.12) 0%, transparent 70%)',
    pointerEvents: 'none', zIndex: 0,
  },
  auroraOrb3: {
    position: 'fixed', top: '40%', left: '30%',
    width: 400, height: 400, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(99,102,241,.08) 0%, transparent 70%)',
    pointerEvents: 'none', zIndex: 0,
  },
  container: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '40px 20px',
    position: 'relative',
    zIndex: 1,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 32,
    flexWrap: 'wrap',
    gap: 16,
  },
  headerBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: 'rgba(249,115,22,.1)', border: '1px solid rgba(249,115,22,.25)',
    borderRadius: 20, padding: '4px 12px', fontSize: 12,
    fontWeight: 700, color: '#ea580c', marginBottom: 10, letterSpacing: '.5px',
  },
  pageTitle: {
    fontSize: 'clamp(26px, 5vw, 36px)',
    fontWeight: 900,
    background: 'linear-gradient(135deg, #1e293b 0%, #7c3aed 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    margin: 0, lineHeight: 1.1,
  },
  pageSubtitle: {
    color: '#64748b', fontSize: 14, marginTop: 6,
  },
  orderCountBadge: {
    background: 'rgba(255,255,255,.75)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(249,115,22,.2)',
    borderRadius: 16,
    padding: '16px 24px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(249,115,22,.1)',
  },
  orderCountNum: {
    display: 'block',
    fontSize: 32, fontWeight: 900,
    background: 'linear-gradient(135deg,#f97316,#7c3aed)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  orderCountLabel: { fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px' },

  /* filter card */
  filterCard: {
    background: 'rgba(255,255,255,.75)',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(255,255,255,.8)',
    borderRadius: 20,
    padding: '20px 24px',
    marginBottom: 28,
    boxShadow: '0 4px 24px rgba(0,0,0,.07)',
  },
  filterRow: { display: 'flex', gap: 20, flexWrap: 'wrap' },
  filterGroup: { flex: 1, minWidth: 200 },
  filterLabel: {
    display: 'flex', alignItems: 'center', gap: 5,
    fontSize: 12, fontWeight: 700, color: '#374151',
    textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8,
  },
  select: {
    width: '100%', padding: '10px 14px',
    border: '1.5px solid rgba(249,115,22,.2)',
    borderRadius: 12, fontSize: 14, color: '#1e293b',
    background: 'rgba(255,255,255,.9)',
    outline: 'none', cursor: 'pointer', fontWeight: 500,
    transition: 'border-color .2s',
    WebkitAppearance: 'none',
  },
  searchWrap: { position: 'relative' },
  searchIcon: {
    position: 'absolute', left: 12, top: '50%',
    transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none',
  },
  searchInput: {
    width: '100%', padding: '10px 14px 10px 38px',
    border: '1.5px solid rgba(249,115,22,.2)',
    borderRadius: 12, fontSize: 14, color: '#1e293b',
    background: 'rgba(255,255,255,.9)',
    outline: 'none', fontWeight: 500,
    boxSizing: 'border-box',
  },

  /* loading / error */
  loadingWrap: { textAlign: 'center', padding: '80px 0' },
  spinner: {
    width: 44, height: 44, margin: '0 auto',
    border: '3px solid rgba(249,115,22,.2)',
    borderTopColor: '#f97316',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorCard: {
    background: 'rgba(254,242,242,.9)', backdropFilter: 'blur(12px)',
    border: '1px solid rgba(239,68,68,.3)', borderRadius: 16,
    padding: '16px 20px', color: '#991b1b', fontWeight: 600,
  },

  /* glass card generic */
  glassCard: {
    background: 'rgba(255,255,255,.75)',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(255,255,255,.8)',
    borderRadius: 24,
    boxShadow: '0 8px 32px rgba(0,0,0,.08)',
  },

  /* order list */
  orderList: { display: 'flex', flexDirection: 'column', gap: 16 },
  orderCard: {
    background: 'rgba(255,255,255,.82)',
    backdropFilter: 'blur(20px)',
    border: '1.5px solid rgba(255,255,255,.7)',
    borderRadius: 22,
    overflow: 'hidden',
    transition: 'all .25s cubic-bezier(.34,1.56,.64,1)',
    cursor: 'pointer',
    position: 'relative',
  },
  cardStripe: {
    position: 'absolute', top: 0, left: 0, right: 0, height: 3,
    background: 'linear-gradient(90deg, #f97316, #7c3aed, #3b82f6)',
    transition: 'opacity .25s',
  },

  /* order header inside card */
  orderHeader: {
    display: 'flex', alignItems: 'flex-start',
    justifyContent: 'space-between', flexWrap: 'wrap',
    gap: 12, padding: '20px 22px 16px',
  },
  orderMeta: { flex: 1, minWidth: 200 },
  orderNumRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 },
  orderIconWrap: {
    width: 32, height: 32, borderRadius: 10,
    background: 'linear-gradient(135deg,rgba(249,115,22,.15),rgba(249,115,22,.05))',
    border: '1px solid rgba(249,115,22,.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  orderNum: { fontSize: 15, fontWeight: 800, color: '#1e293b', margin: 0, fontFamily: 'monospace' },
  orderMetaRow: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  metaChip: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: 12, color: '#64748b', fontWeight: 500,
  },
  metaDot: { color: '#cbd5e1', fontWeight: 900 },
  orderAmount: { textAlign: 'right', flexShrink: 0 },
  amountLabel: { fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 },
  amountValue: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1,
    fontSize: 22, fontWeight: 900,
    background: 'linear-gradient(135deg,#1e293b,#7c3aed)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },

  /* items preview */
  itemsPreview: {
    margin: '0 22px 12px',
    background: 'rgba(248,250,252,.85)',
    border: '1px solid rgba(226,232,240,.8)',
    borderRadius: 14, padding: '12px 14px',
  },
  itemRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, '&:last-child': { marginBottom: 0 } },
  itemImgWrap: {
    width: 48, height: 48, flexShrink: 0,
    borderRadius: 10, overflow: 'hidden',
    background: '#f1f5f9', border: '1px solid #e2e8f0',
    position: 'relative',
  },
  itemImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  itemImgFallback: {
    width: '100%', height: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  itemInfo: { flex: 1, minWidth: 0 },
  itemName: { fontSize: 13, fontWeight: 600, color: '#1e293b', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  itemMeta: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' },
  variantTag: { fontSize: 11, background: '#e2e8f0', color: '#475569', padding: '2px 7px', borderRadius: 6, fontWeight: 600 },
  qtyTag: { fontSize: 11, color: '#64748b', fontWeight: 600 },
  priceTag: { fontSize: 12, color: '#f97316', fontWeight: 700 },
  moreItems: { fontSize: 12, color: '#94a3b8', fontWeight: 600, marginTop: 6, paddingTop: 6, borderTop: '1px dashed #e2e8f0' },

  /* card footer */
  cardFooter: {
    padding: '10px 22px 16px',
    display: 'flex', justifyContent: 'flex-end',
  },
  viewDetailsBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    fontSize: 13, fontWeight: 700, color: '#f97316',
    background: 'rgba(249,115,22,.08)',
    border: '1px solid rgba(249,115,22,.2)',
    borderRadius: 10, padding: '6px 14px',
    transition: 'all .2s',
  },

  /* empty states */
  emptyIcon: {
    width: 80, height: 80, borderRadius: '50%',
    background: 'linear-gradient(135deg,rgba(249,115,22,.15),rgba(249,115,22,.05))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 20px',
  },
  emptyTitle: { fontSize: 20, fontWeight: 800, color: '#1e293b', margin: '0 0 8px' },
  emptySubtitle: { fontSize: 14, color: '#64748b', margin: '0 0 24px' },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'linear-gradient(135deg,#f97316,#ea580c)',
    color: '#fff', fontWeight: 700, fontSize: 14,
    padding: '11px 26px', borderRadius: 14,
    textDecoration: 'none', boxShadow: '0 4px 16px rgba(249,115,22,.35)',
    transition: 'transform .2s, box-shadow .2s',
  },

  /* pagination */
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 36 },
  pageBtn: {
    padding: '8px 18px', borderRadius: 12, border: '1.5px solid rgba(249,115,22,.25)',
    background: 'rgba(255,255,255,.8)', color: '#374151', fontWeight: 700, fontSize: 13,
    cursor: 'pointer', transition: 'all .2s',
  },
  pageNumbers: { display: 'flex', gap: 4 },
  pageNumBtn: {
    width: 38, height: 38, borderRadius: 10, border: 'none',
    fontSize: 14, cursor: 'pointer', transition: 'all .2s', fontWeight: 600,
  },
};

export default OrdersPage;
